import { useState, useEffect } from 'react';
import { JobData } from '../api/JobsService';
import { FormBuilderService } from '../api/FormBuilderService';
import { toast } from 'react-toastify';

export const useJobQuestionnaires = (
  apiJobs: JobData[] | null,
  selectedQuestionnaireId: string | null,
  setSelectedQuestionnaireId: (id: string | null) => void
) => {
  const [jobsWithQuestionnaires, setJobsWithQuestionnaires] = useState<Set<number>>(new Set());
  const [jobQuestionnaireMap, setJobQuestionnaireMap] = useState<Map<number, string>>(new Map());
  const [associatingJobs, setAssociatingJobs] = useState<Set<number>>(new Set());
  const [isOffCanvasOpen, setIsOffCanvasOpen] = useState(false);
  const [isLoadingJobQuestionnaires, setIsLoadingJobQuestionnaires] = useState(false);

  // Check which jobs have questionnaires
  useEffect(() => {
    const checkJobQuestionnaires = async () => {
      if (!apiJobs || apiJobs.length === 0) return;
      
      setIsLoadingJobQuestionnaires(true);
      
      // Create a new set to store jobs with questionnaires
      const jobsWithQuestionnaireSet = new Set<number>();
      const jobQuestionnaireMapTemp = new Map<number, string>();
      
      // Check each job for associated questionnaire
      await Promise.all(apiJobs.map(async (job) => {
        try {
          const result = await FormBuilderService.checkJobQuestionnaire(job.id);
          if (result.hasQuestionnaire) {
            jobsWithQuestionnaireSet.add(job.id);
            
            // Store the questionnaire ID if available
            if (result.data && result.data.questionnaireId) {
              jobQuestionnaireMapTemp.set(job.id, result.data.questionnaireId);
              // console.log(`Job ${job.id} has questionnaire ID: ${result.data.questionnaireId}`);
            }
          }
        } catch (error) {
          console.error(`Error checking questionnaire for job ${job.id}:`, error);
        }
      }));
      
      setJobsWithQuestionnaires(jobsWithQuestionnaireSet);
      setJobQuestionnaireMap(jobQuestionnaireMapTemp);
      setIsLoadingJobQuestionnaires(false);
    };
    
    checkJobQuestionnaires();
  }, [apiJobs]);

  const handleOpenOffCanvas = (questionnaireId: string) => {
    // console.log('Opening job selection off-canvas with Questionnaire ID:', questionnaireId);
    setSelectedQuestionnaireId(questionnaireId);
    setIsOffCanvasOpen(true);
  };

  const handleCloseOffCanvas = () => {
    // console.log('Closing job selection off-canvas, Questionnaire ID was:', selectedQuestionnaireId);
    setIsOffCanvasOpen(false);
  };
  
  // Handler for the Select/Detach button click
  const handleActionClick = async (jobId?: number) => {
    if (!jobId || !selectedQuestionnaireId) return;
    
    // Add this job to the set of jobs being processed - use functional update pattern
    setAssociatingJobs(prevJobs => {
      const newJobs = new Set(prevJobs);
      newJobs.add(jobId);
      return newJobs;
    });
    
    // Safety timeout to ensure button doesn't get stuck in processing state
    const safetyTimeout = setTimeout(() => {
      setAssociatingJobs(prevJobs => {
        if (prevJobs.has(jobId)) {
          const newJobs = new Set(prevJobs);
          newJobs.delete(jobId);
          console.log(`Safety timeout cleared processing state for job ${jobId}`);
          return newJobs;
        }
        return prevJobs;
      });
    }, 10000); // 10 seconds timeout
    
    try {
      // Check if the job already has a questionnaire
      const hasQuestionnaire = jobsWithQuestionnaires.has(jobId);
      
      if (hasQuestionnaire) {
        try {
          // console.log('Detaching questionnaire from job...');
          // Get the currently associated questionnaire ID from the map if available
          const currentQuestionnaireId = jobQuestionnaireMap.get(jobId);
          
          if (currentQuestionnaireId) {
            // console.log(`Using stored questionnaire ID for detaching: ${currentQuestionnaireId}`);
          } else {
            // console.log(`No stored questionnaire ID found for job ${jobId}, using selected ID: ${selectedQuestionnaireId}`);
          }
          
          // Detach the questionnaire - use the mapped ID if available, otherwise use selected ID
          await FormBuilderService.detachQuestionnaireFromJob(jobId, currentQuestionnaireId || selectedQuestionnaireId);
          
          // Update the state to remove this job from jobsWithQuestionnaires
          setJobsWithQuestionnaires(prevJobs => {
            const updated = new Set(prevJobs);
            updated.delete(jobId);
            return updated;
          });
          
          // Also update the map
          setJobQuestionnaireMap(prevMap => {
            const updated = new Map(prevMap);
            updated.delete(jobId);
            return updated;
          });
          
          // Show success notification
          toast.success(`Questionnaire successfully detached from job ID: ${jobId}`);
        } catch (detachError: any) {
          console.error("Error detaching questionnaire:", detachError);
          // Log more detailed information about the error
          if (detachError.response) {
            console.error("Error response status:", detachError.response.status);
            console.error("Error response data:", detachError.response.data);
            console.error("Error response headers:", detachError.response.headers);
            
            // Specific handling for 415 errors
            if (detachError.response.status === 415) {
              console.error("Received 415 Unsupported Media Type - This is likely due to missing or incorrect Content-Type header");
            }
          }
          toast.error(`Failed to detach questionnaire. Please try again.`);
        }
      } else {
        // Associate a questionnaire with the job
        try {
          // console.log('Associating Questionnaire ID:', selectedQuestionnaireId, 'with Job ID:', jobId);
          // Call the service to associate the questionnaire with the job
          await FormBuilderService.associateQuestionnaireWithJob(jobId, selectedQuestionnaireId);
          
          // Update the state to add this job to jobsWithQuestionnaires
          setJobsWithQuestionnaires(prevJobs => {
            const updated = new Set(prevJobs);
            updated.add(jobId);
            return updated;
          });
          
          // Update the map with the new association
          setJobQuestionnaireMap(prevMap => {
            const updated = new Map(prevMap);
            updated.set(jobId, selectedQuestionnaireId);
            return updated;
          });
          
          // Show success notification
          toast.success(`Questionnaire successfully associated with job ID: ${jobId}`);
          // console.log('Association successful');
        } catch (associateError: any) {
          console.error("Error associating questionnaire:", associateError);
          // Log more detailed information about the error
          if (associateError.response) {
            console.error("Error response status:", associateError.response.status);
            console.error("Error response data:", associateError.response.data);
            console.error("Error response headers:", associateError.response.headers);
            
            // Specific handling for 415 errors
            if (associateError.response.status === 415) {
              console.error("Received 415 Unsupported Media Type - This is likely due to missing or incorrect Content-Type header");
            }
          }
          toast.error(`Failed to associate questionnaire. Please try again.`);
        }
      }
    } catch (error) {
      console.error("Error processing questionnaire action for job:", error);
      toast.error(`Failed to process questionnaire action. Please try again.`);
    } finally {
      // Clear the safety timeout
      clearTimeout(safetyTimeout);
      
      // Add a small delay to prevent immediate re-clicking
      setTimeout(() => {
        // Remove this job from the processing set when done - use functional update pattern
        setAssociatingJobs(prevJobs => {
          const newJobs = new Set(prevJobs);
          newJobs.delete(jobId);
          return newJobs;
        });
      }, 300); // 300ms delay for better UX
    }
  };
  
  // Helper function to determine the action button text
  const getActionButtonText = (jobId: number, isActive: boolean) => {
    if (associatingJobs.has(jobId)) {
      return "Processing...";
    }
    
    if (jobsWithQuestionnaires.has(jobId)) {
      return "Detach";
    }
    
    return isActive ? "Select" : "Inactive";
  };

  // Helper function to determine the action button variant
  const getActionButtonVariant = (jobId: number, isActive: boolean): 'outline-primary' | 'outline-danger' | 'transparent' => {
    if (associatingJobs.has(jobId)) {
      return 'transparent';
    }
    
    if (jobsWithQuestionnaires.has(jobId)) {
      return 'outline-danger';
    }
    
    return 'outline-primary';
  };

  return {
    jobsWithQuestionnaires,
    jobQuestionnaireMap,
    associatingJobs,
    isOffCanvasOpen,
    setIsOffCanvasOpen,
    handleOpenOffCanvas,
    handleCloseOffCanvas,
    handleActionClick,
    getActionButtonText,
    getActionButtonVariant,
    isLoadingJobQuestionnaires
  };
}; 